import React, { createContext, useContext } from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { usePanel } from 'modules/finance/components/FinancialWorkspace/components/ResponsivePageWithPanel/provider'

import { HistoryPanel } from '../../components/HistoryPanel'
import { useAgreementsData } from '../../hooks/useAgreementsData'
import { useExpensesData } from '../../hooks/useExpensesData'
import { usePayrollData } from '../../hooks/usePayrollData'
import { HookData } from '../../types/data'

interface Props {
  children: React.ReactNode
}

interface IValue {
  loading: boolean
  payrollData: HookData
  expensesData: HookData
  agreementData: HookData
}

const DataProviderContext = createContext<IValue | undefined>(undefined)

export const DataProvider: React.FC<Props> = ({ children }) => {
  const { formattedState } = useFinancialWorkspace()

  const { setContent } = usePanel()

  const agreementData: HookData = useAgreementsData()
  const expensesData: HookData = useExpensesData()
  const payrollData: HookData = usePayrollData()

  useUnsafeEffect(() => {
    setContent(<HistoryPanel formattedState={formattedState} />)
  }, [setContent, formattedState])

  return (
    <DataProviderContext.Provider
      value={{
        payrollData,
        expensesData,
        agreementData,
        loading: !!(expensesData?.loading || payrollData?.loading || agreementData?.loading),
      }}
    >
      {children}
    </DataProviderContext.Provider>
  )
}

export const useData = () => {
  const context = useContext(DataProviderContext)
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider')
  }
  return context
}
